"use client";

import { useConsent } from "@packages/consent";
import {
  getCookieAjsAnonymousId,
  setCookieAjsAnonymousId,
  useRequestMe,
  useSession,
} from "@packages/sdk";
import mixpanelBrowser from "mixpanel-browser";
import type { PropsWithChildren } from "react";
import { useCallback, useEffect, useState } from "react";

import type { MixpanelWithFlags } from "../lib/mixpanelContext";
import { mixpanelContext } from "../lib/mixpanelContext";
import { HallowABFlag } from "../types/enums/HallowABFlag.enum";
import { MixpanelABFlagValidProps } from "../types/enums/MixpanelABFlagValidProps.enum";

const THREE_SECONDS_IN_MS = 3000;
const MS_BETWEEN_ATTEMPTS = 250;
const ATTEMPTS = THREE_SECONDS_IN_MS / MS_BETWEEN_ATTEMPTS;

export const MixpanelABProvider = ({ children }: PropsWithChildren) => {
  const [mixpanel, setMixpanel] = useState<MixpanelWithFlags | null>(null);
  const [ready, setReady] = useState<boolean>(false);
  const [error, setError] = useState<boolean>(false);
  const [hasIdentified, setHasIdentified] = useState<boolean>(false);
  const { query: queryMe } = useRequestMe();
  const { isConsenting, isLoading: isConsentLoading } = useConsent();
  useSession({
    onUnauthenticated: () => {
      mixpanel?.reset();
      setHasIdentified(false);
    },
  });

  useEffect(() => {
    if (mixpanel && queryMe.data?.id && !hasIdentified) {
      mixpanel.identify(queryMe.data.id.toString());
      setHasIdentified(true);
    }
  }, [queryMe.data, hasIdentified, mixpanel]);

  useEffect(() => {
    if (!isConsentLoading && isConsenting) {
      try {
        mixpanelBrowser.init(process.env.DOPPLER_PUBLIC_MIXPANEL_TOKEN, {
          debug: process.env.NODE_ENV === "development",
          // @ts-ignore -- this is in the JS code, but not the types
          flags: {
            custom_properties: {
              [MixpanelABFlagValidProps[
                HallowABFlag.FamilyPlanOneClickUpgrade
              ]]: false,
            },
          },
          loaded: (mp) => {
            setMixpanel(mp as MixpanelWithFlags);
            const ajsAnonymousId = getCookieAjsAnonymousId();
            if (ajsAnonymousId) {
              mp.alias(ajsAnonymousId);
            } else {
              setCookieAjsAnonymousId({ value: mp.get_distinct_id() });
            }
          },
        });
      } catch (e) {
        console.error(e);
        setError(true);
      }
    } else if (!isConsentLoading && !isConsenting) {
      setError(true);
    }
  }, [isConsenting, isConsentLoading]);

  useEffect(() => {
    let interval = null;
    if (mixpanel) {
      if (mixpanel.flags.are_flags_ready()) {
        setReady(true);
      } else {
        let count = 0;
        interval = setInterval(() => {
          if (++count > ATTEMPTS) {
            setError(true);
            clearInterval(interval);
          } else if (mixpanel.flags?.are_flags_ready()) {
            setReady(true);
            clearInterval(interval);
          }
        }, MS_BETWEEN_ATTEMPTS);
      }
    }

    return () => {
      clearInterval(interval);
    };
  }, [mixpanel]);

  const updateContext = (context: Record<string, unknown>) => {
    if (ready) mixpanel.flags.update_context(context);
  };

  const getVariant = useCallback(
    (key: HallowABFlag, defaultValue: "control" | "treatment" | "excluded") => {
      if (!ready) return Promise.resolve(defaultValue);
      return mixpanel.flags.get_variant_value(key, defaultValue);
    },
    [ready, mixpanel],
  );

  return (
    <mixpanelContext.Provider
      value={{
        getVariant,
        isLoading: !ready && !error,
        updateContext,
      }}
    >
      {children}
    </mixpanelContext.Provider>
  );
};
